import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UIHelpers } from '../../../core/ui-helpers';
import { TransactionFacade } from '../../../models/transactions/transactionFacade';
import { TransactionService } from '../../../models/transactions/transactionService';
import { UIErrorHandler } from '../../../core/uiErrorHandler';
import { OrderService } from '../../../models/orders/orderService';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomSnackbarComponent } from '../../../components/ui/snackbar/custom-snackbar.component';

@Component({
  selector: 'app-transaction-details',
  templateUrl: './transaction-details.component.html',
  standalone: false,
})
export class TransactionDetailsComponent implements OnChanges {
  _transaction: Partial<TransactionFacade> | undefined;
  loading: boolean = false;
  canCancelRecurringPayment: boolean = false;
  sameDayTransaction: boolean = false;

  @Output() onSaleReversal = new EventEmitter<void>();
  @Output() onSaleReturn = new EventEmitter<void>();
  @Output() onSaleVoid = new EventEmitter<void>();
  @Output() onSaleCapture = new EventEmitter<void>();

  @Input() set transaction(value: Partial<TransactionFacade> | undefined) {
    if (value) {
      this._transaction = value;
      const today = new Date().toISOString().split('T')[0];
      if (this._transaction?.date?.split('T')[0] == today) {
        this.sameDayTransaction = true;
      } else {
        this.sameDayTransaction = false;
      }
      if (
        this._transaction?.type == 'RECURRING' ||
        this._transaction?.type == 'VERIFY'
      ) {
        this.canCancelRecurringPayment = true;
      }
    }
  }

  disableCancelRecurringPayment: boolean = false;

  constructor(
    private transactionService: TransactionService,
    private orderService: OrderService,
    public uiHelpers: UIHelpers,
    public snackBar: MatSnackBar,
    private uiErrorHandler: UIErrorHandler,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['id']) {
      this.transactionService
        .getTransaction(changes['id'].currentValue)
        .then((t) => {
          this.transaction = t;
          const today = new Date().toISOString().split('T')[0];
          if (this._transaction?.date?.split('T')[0] == today) {
            this.sameDayTransaction = true;
          } else {
            this.sameDayTransaction = false;
          }
          if (
            this._transaction?.type == 'RECURRING' ||
            this._transaction?.type == 'VERIFY'
          ) {
            this.canCancelRecurringPayment = true;
          }
          //this.initProperties();
        })
        .catch((message) => {
          this.uiErrorHandler.handleError(message);
        });
    }
  }

  sendTransactionToCustomer() {
    this.loading = true;
    this.orderService
      .getOrder(this._transaction?.orderId!)
      .then((order) => {
        this.transactionService
          .sendTransactionToCustomer(
            order.recipientEmail!,
            order,
            this._transaction?.type!,
          )
          .then((response) => {
            this.loading = false;
            this.snackBar?.openFromComponent(CustomSnackbarComponent, {
              duration: 5000,
              panelClass: 'snackbar-success',
              data: {
                message: 'A transaction email has been sent to the customer.',
                fontSize: '1.5em',
                iconSize: '50px',
              },
            });
          })
          .catch((error) => {
            this.loading = false;
            this.snackBar?.openFromComponent(CustomSnackbarComponent, {
              duration: 5000,
              panelClass: 'snackbar-error',
              data: {
                message:
                  'There was a problem sending the transaction to the customer: ' +
                  error,
                fontSize: '1.5em',
                iconSize: '50px',
              },
            });
          });
      })
      .catch((error) => {
        this.loading = false;
        this.uiErrorHandler.handleError(error);
      });
  }

  formatDate(val: string): string {
    return UIHelpers.formatDate(val);
  }

  allowTransactionMethod(transactionType: string): boolean {
    return false;
  }

  beginSaleReversal() {
    this.onSaleReversal.emit();
  }
  beginSaleReturn() {
    if (
      this._transaction?.maxRefundAmount! != this._transaction?.magnifiedAmount!
    ) {
      const refundedAmount = this.uiHelpers.formatCurrencyToString(
        this.uiHelpers.demagnifyAmount(
          this._transaction?.magnifiedAmount! -
            this._transaction?.maxRefundAmount!,
          this._transaction?.magnifier!,
        ),
      );
      if (
        confirm(
          `This transaction has already been partially refunded for: ${refundedAmount}. Are you sure you want to refund the remaining amount?`,
        )
      ) {
        this.onSaleReturn.emit();
      }
    } else {
      this.onSaleReturn.emit();
    }
  }
  beginSaleVoid() {
    this.onSaleVoid.emit();
  }
  beginSaleCapture() {
    this.onSaleCapture.emit();
  }

  cancelRecurringPayment() {
    this.disableCancelRecurringPayment = true;
    this.loading = true;
    this.transactionService
      .cancelRecurringPayment(
        this._transaction?.orderId!,
        this._transaction?.type!,
      )
      .then(() => {
        this.loading = false;
        this.snackBar?.openFromComponent(CustomSnackbarComponent, {
          duration: 5000,
          panelClass: 'snackbar-success',
          data: {
            message: 'Recurring payment has been cancelled.',
            fontSize: '1.5em',
            iconSize: '50px',
          },
        });
      })
      .catch((error) => {
        this.loading = false;
        this.disableCancelRecurringPayment = false;
        this.snackBar?.openFromComponent(CustomSnackbarComponent, {
          duration: 5000,
          panelClass: 'snackbar-error',
          data: {
            message:
              'There was a problem canceling the recurring payment: ' + error,
            fontSize: '1.5em',
            iconSize: '50px',
          },
        });
      });
  }
}
